import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FiDatabase, FiTable } from 'react-icons/fi';
import { Form, Modal } from 'react-bootstrap';
import DatabaseService from '../../services/api/database';
import DOMPurify from 'dompurify';
import { showNotificationGlobal } from '../common/NotificationProvider';
import styles from './DatabaseComponent.module.css';

const getInitialColumnWidth = (column) => {
  const normalized = String(column || '').toLowerCase();

  if (normalized === 'id' || normalized.endsWith('_id')) return 240;
  if (normalized.includes('password')) return 360;
  if (normalized.includes('email') || normalized.includes('name')) return 240;
  if (normalized.includes('_at') || normalized.includes('date')) return 240;
  if (normalized.startsWith('is_')) return 110;
  return 160;
};

const renderMongoJsonValue = (value, depth = 0) => {
  if (value === null || value === undefined) {
    return <span className={styles.jsonNull}>null</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className={styles.jsonBrace}>[]</span>;

    return (
      <details className={styles.jsonExpandable}>
        <summary className={styles.jsonSummary}>
          <span className={styles.jsonCaret} />
          <span className={styles.jsonBrace}>[...{value.length}]</span>
        </summary>
        <div className={styles.jsonExpandedBlock}>
          <div className={styles.jsonLine}>
            <span className={styles.jsonBrace}>[</span>
          </div>
          {value.map((item, index) => (
            <div key={`${depth}-${index}`} className={styles.jsonLine}>
              <span className={styles.jsonIndent} />
              {renderMongoJsonValue(item, depth + 1)}
              {index < value.length - 1 && <span className={styles.jsonComma}>,</span>}
            </div>
          ))}
          <div className={styles.jsonLine}>
            <span className={styles.jsonBrace}>]</span>
          </div>
        </div>
      </details>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className={styles.jsonBrace}>{'{}'}</span>;

    return (
      <details className={styles.jsonExpandable}>
        <summary className={styles.jsonSummary}>
          <span className={styles.jsonCaret} />
          <span className={styles.jsonBrace}>{'{...}'}</span>
        </summary>
        <div className={styles.jsonExpandedBlock}>
          <div className={styles.jsonLine}>
            <span className={styles.jsonBrace}>{'{'}</span>
          </div>
          {entries.map(([key, nestedValue], index) => (
            <div key={`${depth}-${key}`} className={styles.jsonLine}>
              <span className={styles.jsonIndent} />
              <span className={styles.jsonKey}>{JSON.stringify(key)}</span>
              <span className={styles.jsonColon}>: </span>
              {renderMongoJsonValue(nestedValue, depth + 1)}
              {index < entries.length - 1 && <span className={styles.jsonComma}>,</span>}
            </div>
          ))}
          <div className={styles.jsonLine}>
            <span className={styles.jsonBrace}>{'}'}</span>
          </div>
        </div>
      </details>
    );
  }

  if (typeof value === 'string') {
    return <span className={styles.jsonString}>{JSON.stringify(value)}</span>;
  }

  if (typeof value === 'number') {
    return <span className={styles.jsonNumber}>{value}</span>;
  }

  if (typeof value === 'boolean') {
    return <span className={styles.jsonBoolean}>{String(value)}</span>;
  }

  return <span className={styles.jsonNestedValue}>{JSON.stringify(value)}</span>;
};

const renderMongoJsonDocument = (row) => {
  const entries = Object.entries(row || {});

  return (
    <div className={styles.mongoJsonDocumentCode}>
      <div className={styles.jsonLine}>
        <span className={styles.jsonBrace}>{'{'}</span>
      </div>
      {entries.map(([key, value], index) => (
        <div key={key} className={styles.jsonLine}>
          <span className={styles.jsonIndent} />
          <span className={styles.jsonKey}>{JSON.stringify(key)}</span>
          <span className={styles.jsonColon}>: </span>
          {renderMongoJsonValue(value)}
          {index < entries.length - 1 && <span className={styles.jsonComma}>,</span>}
        </div>
      ))}
      <div className={styles.jsonLine}>
        <span className={styles.jsonBrace}>{'}'}</span>
      </div>
    </div>
  );
};

const DatabaseComponent = ({ projectId, onConnectionChange }) => {
  const [databaseInfo, setDatabaseInfo] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showSchemaModal, setShowSchemaModal] = useState(false);
  const [tableSchema, setTableSchema] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaFormat, setSchemaFormat] = useState('columns');
  const [error, setError] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [dataViewMode, setDataViewMode] = useState('table');

  // Ref to track if component is mounted (prevents memory leaks)
  const isMountedRef = useRef(true);
  const onConnectionChangeRef = useRef(onConnectionChange);
  const fetchInfoInFlightRef = useRef(false);
  const lastFetchedProjectIdRef = useRef(null);

  useEffect(() => {
    onConnectionChangeRef.current = onConnectionChange;
  }, [onConnectionChange]);

  // Sanitize string to prevent XSS
  const sanitizeInput = useCallback((input) => {
    if (typeof input !== 'string') return input;
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  }, []);


  const fetchDatabaseTables = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await DatabaseService.getDatabaseTables(projectId);
      if (isMountedRef.current) {
        const sanitizedTables = (response || []).map(table => ({
          ...table,
          table_name: sanitizeInput(table.table_name),
          schema: sanitizeInput(table.schema)
        }));
        setTables(sanitizedTables);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to fetch tables');
        console.error('Fetch tables error:', err);
      }
    }
  }, [sanitizeInput, projectId]);

  const fetchTablesData = useCallback(async (tableName) => {
    if (!isMountedRef.current || !tableName) return;
    
    try {
      const sanitizedTableName = sanitizeInput(tableName);
      const response = await DatabaseService.getTableData(sanitizedTableName, projectId);
      
      if (isMountedRef.current) {
        setTableData(response?.result || []);
        setLoading(false);
        setError(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to fetch table data');
        setTableData([]);
        setLoading(false);
        console.error('Fetch table data error:', err);
      }
    }
  }, [sanitizeInput, projectId]);

  const fetchDatabaseInfo = useCallback(async () => {
    if (!isMountedRef.current) return;
    if (!projectId) return;
    if (fetchInfoInFlightRef.current) return;
    if (lastFetchedProjectIdRef.current === projectId) return;
    
    try {
      fetchInfoInFlightRef.current = true;
      lastFetchedProjectIdRef.current = projectId;
      const response = await DatabaseService.getDatabaseInfo(projectId);
      
      if (!isMountedRef.current) return;

      if (response?.length > 0) {
        const sanitizedResponse = response.map(db => ({
          ...db,
          status: "Connected",
          name: sanitizeInput(db?.database_name),
          database_type: sanitizeInput(db?.database_type)
        }));
        setDatabaseInfo(sanitizedResponse);
        onConnectionChangeRef.current?.(true);
        fetchDatabaseTables();
      } else {
        setDatabaseInfo([{ status: "Failed" }]);
        onConnectionChangeRef.current?.(false);
      }
      setError(null);
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to fetch database info');
        setDatabaseInfo([{ status: "Failed" }]);
        onConnectionChangeRef.current?.(false);
        console.error('Database info error:', err);
      }
    } finally {
      fetchInfoInFlightRef.current = false;
    }
  }, [sanitizeInput, fetchDatabaseTables, projectId]);

  useEffect(() => {
    isMountedRef.current = true;
    // only reset dedupe when project actually changes
    if (lastFetchedProjectIdRef.current !== projectId) {
      lastFetchedProjectIdRef.current = null;
    }
    fetchDatabaseInfo();

    return () => {
      isMountedRef.current = false;
    };
  }, [projectId, fetchDatabaseInfo]);

  const handleTableSelect = useCallback((table) => {
    if (!table) return;
    setSelectedTable(table);
    setLoading(true);
    setCurrentPage(1);
    fetchTablesData(table?.table_name);
  }, [fetchTablesData]);

  const refreshDatabase = useCallback(() => {
    if (!selectedTable) return;
    setLoading(true);
    setTimeout(() => {
      if (isMountedRef.current) {
        fetchTablesData(selectedTable?.table_name);
      }
    }, 1000);
  }, [selectedTable, fetchTablesData]);

  const handleViewSchema = useCallback(async () => {
    if (!selectedTable || !isMountedRef.current) return;
    
    setShowSchemaModal(true);
    setSchemaLoading(true);
    
    try {
      const response = await DatabaseService.getTableSchema(
        sanitizeInput(selectedTable?.table_name),
        sanitizeInput(selectedTable?.schema),
        projectId
      );
      
      if (!isMountedRef.current) return;

      if (response && response.length > 0 && response[0]?.table_schema) {
        setSchemaFormat('sql');
        setTableSchema(sanitizeInput(response[0].table_schema));
      } else if (Array.isArray(response) && response.length > 0 && response[0]?.column_name) {
        setSchemaFormat('columns');
        const sanitizedSchema = response.map(col => ({
          ...col,
          column_name: sanitizeInput(col.column_name),
          data_type: sanitizeInput(col.data_type),
          constraint_type: sanitizeInput(col.constraint_type)
        }));
        setTableSchema(sanitizedSchema);
      } else {
        setSchemaFormat('columns');
        setTableSchema(response || []);
      }
      setError(null);
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to fetch schema');
        setSchemaFormat('columns');
        setTableSchema([]);
        console.error('Fetch schema error:', err);
      }
    } finally {
      if (isMountedRef.current) {
        setSchemaLoading(false);
      }
    }
  }, [selectedTable, sanitizeInput, projectId]);

  const handleCloseSchemaModal = useCallback(() => {
    setShowSchemaModal(false);
    setTableSchema([]);
    setSchemaFormat('columns');
  }, []);

  // Memoized pagination calculations
  const currentPageData = useMemo(() => {
    if (!tableData || !selectedTable) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return tableData.slice(startIndex, endIndex);
  }, [tableData, selectedTable, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    if (!selectedTable || !tableData || tableData.length === 0) return 0;
    return Math.ceil(tableData.length / itemsPerPage);
  }, [selectedTable, tableData, itemsPerPage]);

  const columns = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];
    return Object.keys(tableData[0]);
  }, [tableData]);

  useEffect(() => {
    const initialWidths = columns.reduce((acc, column) => ({
      ...acc,
      [column]: getInitialColumnWidth(column)
    }), {});
    setColumnWidths(initialWidths);
  }, [columns]);

  const handleColumnResizeStart = useCallback((event, column) => {
    event.preventDefault();
    event.stopPropagation();

    const headerCell = event.currentTarget.closest('th');
    const startX = event.clientX;
    const startWidth = headerCell?.offsetWidth || columnWidths[column] || 160;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent) => {
      const nextWidth = Math.max(80, startWidth + moveEvent.clientX - startX);
      setColumnWidths((prev) => ({
        ...prev,
        [column]: nextWidth
      }));
    };

    const handleMouseUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  }, [totalPages]);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const renderValue = useCallback((value) => {
    if (value === null || value === undefined) {
      return <span className={styles.nullValue}>null</span>;
    }
    if (typeof value === 'object') {
      return sanitizeInput(JSON.stringify(value));
    }
    return sanitizeInput(String(value));
  }, [sanitizeInput]);

  const copyRowAsJson = useCallback(async (row) => {
    const text = JSON.stringify(row ?? {}, null, 2);
    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    };

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        fallbackCopy();
      }
      showNotificationGlobal('Row copied to clipboard', 'success', 'bottomRight');
    } catch {
      try {
        fallbackCopy();
        showNotificationGlobal('Row copied to clipboard', 'success', 'bottomRight');
      } catch {
        showNotificationGlobal('Could not copy to clipboard', 'error', 'bottomRight');
      }
    }
  }, []);

  return (
    <div className={styles.databaseComponent}>
      {/* Database Header */}
      <div className={styles.databaseHeader}>
        <div className={styles.headerRow}>
          <div className={styles.databaseInfo}>
            <div className={styles.dbIcon}>
              <FiDatabase size={18} />
            </div>
            <div className={styles.dbDetails}>
              <h2 className={styles.dbName}>
                {databaseInfo[0]?.name || "N/A"}
              </h2>
            </div>
          </div>

          <div className={styles.statusActions}>
            <div className={`${styles.statusBadge} ${databaseInfo[0]?.status === "Connected" ? styles.connected : styles.failed}`}>
              <div className={`${styles.statusDot} ${databaseInfo[0]?.status === "Connected" ? styles.connected : styles.failed}`} />
              {databaseInfo[0]?.status || "Failed"}
            </div>
            
            <button
              onClick={refreshDatabase}
              disabled={loading || !selectedTable}
              className={styles.refreshButton}
              title="Refresh table data"
              aria-label="Refresh table data"
            >
              <i className="fa-solid fa-sync" style={{ 
                fontSize: '12px', 
                animation: loading ? 'spin 1s linear infinite' : 'none' 
              }} />
            </button>
          </div>
        </div>

        {/* Table Selector */}
        <div className={styles.tableSelector}>
          <div className={styles.tableLabel}>
            <FiTable size={14} />
            <span>Table:</span>
          </div>
          
          <Form.Select
            value={selectedTable?.table_name || ''}
            onChange={(e) => {
              const tableId = e.target.value;
              const table = tables.find(t => t.table_name === tableId);
              if (table) handleTableSelect(table);
            }}
            size="sm"
            className={styles.tableDropdown}
            aria-label="Select database table"
          >
            <option value="">
              {tables.length === 0 ? 'No tables available' : 'Select a table...'}
            </option>
            {tables.map((table) => (
              <option key={table.table_name} value={table.table_name}>
                {table.table_name} ({table?.row_estimate?.toLocaleString() || 0} rows • {table?.total_size || 'N/A'})
              </option>
            ))}
          </Form.Select>

          {selectedTable && (
            <>
              <div className={styles.divider} />
              
              <div className={styles.tableStats}>
                <div className={styles.statItem}>
                  <i className="fa-solid fa-list" style={{ fontSize: '10px' }} />
                  <span>{selectedTable.row_estimate?.toLocaleString() || 0}</span>
                </div>
                
                <div className={styles.statItem}>
                  <i className="fa-solid fa-database" style={{ fontSize: '10px' }} />
                  <span>{selectedTable?.total_size || 'N/A'}</span>
                </div>

                <button
                  onClick={handleViewSchema}
                  className={styles.schemaButton}
                  title="View table schema"
                  aria-label="View table schema"
                >
                  <i className="fa-solid fa-code" style={{ fontSize: '10px' }} />
                  <span>View Schema</span>
                </button>
              </div>
            </>
          )}

          {tables.length > 0 && (
            <div className={styles.tableSelectorActions}>
              {selectedTable && tableData.length > 0 && (
                <div className={styles.dataViewToolbar}>
                  <span className={styles.dataViewLabel}>View</span>
                  <div className={styles.dataViewToggle} role="group" aria-label="Data view mode">
                    <button
                      type="button"
                      className={`${styles.dataViewButton} ${dataViewMode === 'table' ? styles.activeDataViewButton : ''}`}
                      onClick={() => setDataViewMode('table')}
                      aria-pressed={dataViewMode === 'table'}
                      title="Table view"
                    >
                      <i className="fa-solid fa-table" />
                    </button>
                    <button
                      type="button"
                      className={`${styles.dataViewButton} ${dataViewMode === 'json' ? styles.activeDataViewButton : ''}`}
                      onClick={() => setDataViewMode('json')}
                      aria-pressed={dataViewMode === 'json'}
                      title="JSON view"
                    >
                      <i className="fa-solid fa-code" />
                    </button>
                  </div>
                </div>
              )}
              <div className={styles.tableCount}>
                {tables.length} {tables.length === 1 ? 'table' : 'tables'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Table Content */}
      <div className={styles.tableContent}>
        {error && (
          <div className={styles.errorMessage} role="alert">
            {error}
          </div>
        )}

        {selectedTable ? (
          <div className={styles.tableDataWrapper}>
            {loading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner} role="status" aria-label="Loading" />
                <p>Loading table data...</p>
              </div>
            ) : (
              <div className={styles.dataTable}>
                {dataViewMode === 'json' ? (
                  <div className={styles.jsonDocuments}>
                    {currentPageData.map((row, rowIndex) => (
                      <div key={rowIndex} className={styles.jsonDocument}>
                        <button
                          type="button"
                          className={styles.rowCopyButtonJson}
                          title="Copy row as JSON"
                          aria-label="Copy row as JSON"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyRowAsJson(row);
                          }}
                        >
                          <i className="fa-regular fa-copy" />
                        </button>
                        <div className={styles.jsonDocumentIndex}>
                          {(currentPage - 1) * itemsPerPage + rowIndex + 1}
                        </div>
                        {renderMongoJsonDocument(row)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.tableWrapper}>
                    <table className={`${styles.table} ${styles.resizableTable}`}>
                      <colgroup>
                        <col style={{ width: 40, minWidth: 40 }} />
                        {columns.map((column) => (
                          <col
                            key={column}
                            style={columnWidths[column] ? { width: `${columnWidths[column]}px` } : undefined}
                          />
                        ))}
                      </colgroup>
                      <thead className={styles.tableHeader}>
                        <tr>
                          <th className={styles.tableHeaderCell} style={{ width: 40, minWidth: 40, maxWidth: 40, padding: '10px 4px' }} aria-label="Copy row" />
                          {columns.map((column, index) => (
                            <th
                              key={`${column}-${index}`}
                              className={styles.tableHeaderCell}
                              style={columnWidths[column] ? {
                                width: `${columnWidths[column]}px`,
                                minWidth: `${columnWidths[column]}px`
                              } : undefined}
                            >
                              <span className={styles.tableHeaderLabel}>{column}</span>
                              <button
                                type="button"
                                className={styles.columnResizeHandle}
                                onMouseDown={(event) => handleColumnResizeStart(event, column)}
                                aria-label={`Resize ${column} column`}
                                title="Drag to resize column"
                              />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {currentPageData.map((row, rowIndex) => (
                          <tr key={rowIndex} className={styles.tableRow}>
                            <td className={styles.tableActionCell}>
                              <button
                                type="button"
                                className={styles.rowCopyButtonTable}
                                title="Copy row as JSON"
                                aria-label="Copy row as JSON"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyRowAsJson(row);
                                }}
                              >
                                <i className="fa-regular fa-copy" />
                              </button>
                            </td>
                            {columns.map((column, colIndex) => (
                              <td
                                key={`${rowIndex}-${colIndex}`}
                                className={styles.tableCell}
                                style={columnWidths[column] ? {
                                  width: `${columnWidths[column]}px`,
                                  minWidth: `${columnWidths[column]}px`,
                                  maxWidth: `${columnWidths[column]}px`
                                } : undefined}
                              >
                                {renderValue(row[column])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Pagination Controls */}
                {tableData.length > 0 && (
                  <div className={styles.pagination}>
                    <div className={styles.paginationLeft}>
                      <span>Rows per page:</span>
                      <Form.Select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
                        size="sm"
                        className={styles.rowsPerPageSelect}
                        aria-label="Rows per page"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Form.Select>
                      <span style={{ marginLeft: '8px' }}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, tableData.length)} of {tableData.length} rows
                      </span>
                    </div>

                    <div className={styles.paginationRight}>
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className={styles.pageButton}
                        title="First page"
                        aria-label="First page"
                      >
                        <i className="fa-solid fa-angles-left" style={{ fontSize: '12px' }} />
                      </button>
                      
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={styles.pageButton}
                        title="Previous page"
                        aria-label="Previous page"
                      >
                        <i className="fa-solid fa-chevron-left" style={{ fontSize: '12px' }} />
                      </button>

                      <div className={styles.pageInfo}>
                        <span>Page</span>
                        <input
                          type="number"
                          min={1}
                          max={totalPages}
                          value={currentPage}
                          onChange={(e) => {
                            const page = parseInt(e.target.value, 10);
                            if (!isNaN(page)) {
                              handlePageChange(page);
                            }
                          }}
                          className={styles.pageInput}
                          aria-label="Current page"
                        />
                        <span>of {totalPages}</span>
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={styles.pageButton}
                        title="Next page"
                        aria-label="Next page"
                      >
                        <i className="fa-solid fa-chevron-right" style={{ fontSize: '12px' }} />
                      </button>

                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className={styles.pageButton}
                        title="Last page"
                        aria-label="Last page"
                      >
                        <i className="fa-solid fa-angles-right" style={{ fontSize: '12px' }} />
                      </button>
                    </div>
                  </div>
                )}
                
                {tableData.length === 0 && (
                  <div className={styles.emptyState}>
                    <FiTable size={32} style={{ opacity: 0.5 }} />
                    <p>No data available for this table</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <FiDatabase size={48} style={{ opacity: 0.3 }} />
            <div className={styles.emptyStateContent}>
              <h3 className={styles.emptyStateTitle}>Select a Table</h3>
              <p className={styles.emptyStateText}>
                Choose a table from the list to view its data and structure
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Schema Modal */}
      <Modal 
        show={showSchemaModal} 
        onHide={handleCloseSchemaModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <i className="fa-solid fa-code" style={{ color: '#3b82f6' }} />
            <span>
              Table Schema: <span style={{ fontWeight: '500', color: '#006ef5' }}>
                {selectedTable?.schema}.{selectedTable?.table_name}
              </span>
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {schemaLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} role="status" aria-label="Loading schema" />
              <p>Loading schema...</p>
            </div>
          ) : schemaFormat === 'sql' && tableSchema ? (
            <div className={styles.schemaCode}>
              <pre className={styles.codeBlock}>
                <code>{tableSchema}</code>
              </pre>
            </div>
          ) : tableSchema && tableSchema.length > 0 ? (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead className={styles.tableHeader}>
                  <tr>
                    <th className={styles.tableHeaderCell}>Column Name</th>
                    <th className={styles.tableHeaderCell}>Data Type</th>
                    <th className={styles.tableHeaderCell} style={{ textAlign: 'center' }}>Nullable</th>
                    <th className={styles.tableHeaderCell}>Default</th>
                    <th className={styles.tableHeaderCell}>Key</th>
                  </tr>
                </thead>
                <tbody>
                  {tableSchema.map((column, index) => (
                    <tr key={index} className={styles.tableRow}>
                      <td className={styles.tableCell} style={{ fontWeight: '600', fontFamily: 'monospace' }}>
                        {column.column_name}
                      </td>
                      <td className={styles.tableCell} style={{ fontFamily: 'monospace' }}>
                        <span style={{
                          backgroundColor: '#f1f5f9',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {column.data_type}
                        </span>
                      </td>
                      <td className={styles.tableCell} style={{ textAlign: 'center' }}>
                        {column.is_nullable === 'YES' ? (
                          <span style={{ color: '#10b981', fontSize: '12px', fontWeight: '600' }}>
                            <i className="fa-solid fa-check" />
                          </span>
                        ) : (
                          <span style={{ color: '#ef4444', fontSize: '12px', fontWeight: '600' }}>
                            <i className="fa-solid fa-times" />
                          </span>
                        )}
                      </td>
                      <td className={styles.tableCell} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {column.column_default || <span className={styles.nullValue}>null</span>}
                      </td>
                      <td className={styles.tableCell}>
                        {column.constraint_type && (
                          <span style={{
                            backgroundColor: column.constraint_type === 'PRIMARY KEY' ? '#dbeafe' : '#fef3c7',
                            color: column.constraint_type === 'PRIMARY KEY' ? '#1e40af' : '#92400e',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            {column.constraint_type}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <i className="fa-solid fa-code" style={{ fontSize: '32px', opacity: 0.5 }} />
              <p>No schema information available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <button onClick={handleCloseSchemaModal} className={styles.closeButton}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default React.memo(DatabaseComponent);
